import { Check, Circle, LockKeyhole, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { EQMS_STATUSES, type EqmsStatus } from '../../types/eqms.types';

const LABELS: Record<EqmsStatus, string> = {
  draft: 'Draft',
  in_review: 'In review',
  approved: 'Approved',
  effective: 'Effective',
  obsolete: 'Obsolete',
  discarded: 'Discarded',
};

interface DocumentLifecycleRailProps {
  status: EqmsStatus;
  compact?: boolean;
}

export function DocumentLifecycleRail({ status, compact = false }: DocumentLifecycleRailProps) {
  // A discarded draft left the path before review; drawing its steps would
  // suggest it can still progress.
  if (status === 'discarded') {
    return (
      <div className={cn('flex items-center gap-2 text-slate-500', compact ? 'text-xs' : 'text-sm')}>
        <XCircle className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
        <span>Discarded before review</span>
      </div>
    );
  }
  const currentIndex = EQMS_STATUSES.indexOf(status);

  return (
    <div className={cn('grid grid-cols-5', compact ? 'gap-1' : 'gap-2')}>
      {EQMS_STATUSES.map((step, index) => {
        const reached = index <= currentIndex;
        const current = step === status;
        return (
          <div key={step} className="relative min-w-0">
            {index < EQMS_STATUSES.length - 1 && (
              <div
                className={cn(
                  'absolute left-1/2 right-[-50%] top-3 h-px',
                  index < currentIndex ? 'bg-teal-500' : 'bg-slate-200',
                )}
              />
            )}
            <div className="relative z-10 flex flex-col items-center text-center">
              <div
                className={cn(
                  'flex h-6 w-6 items-center justify-center rounded-full border bg-white',
                  reached && 'border-teal-600 bg-teal-600 text-white',
                  current && 'ring-4 ring-teal-100',
                  status === 'obsolete' && current && 'border-slate-500 bg-slate-500 ring-slate-100',
                )}
              >
                {step === 'effective' && reached
                  ? <LockKeyhole className="h-3 w-3" />
                  : reached ? <Check className="h-3 w-3" /> : <Circle className="h-2.5 w-2.5" />}
              </div>
              {!compact && (
                <span className={cn('mt-2 text-[11px] text-slate-500', current && 'font-semibold text-slate-900')}>
                  {LABELS[step]}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
